import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { EntitlementsCard } from '../components/EntitlementsCard';
import { PlanComparisonDialog } from '../components/PlanComparisonDialog';
import ContactSalesModal from '../components/landingpage/ContactSalesModal';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  CreditCard,
  Download,
  Star,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  XCircle
} from 'lucide-react';
import { formatBillingDate, getStatusBadge } from '@/helpers/common';
import { api } from '../api';
import { Package, Invoice, BillingAddressData, Subscription } from '../api/billing';
import { useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { COUNTRIES } from '@/helpers/collections';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentForm, PaymentMethodCard } from '../components/PaymentForm';
import stripePromise from '../config/stripe';

interface BillingProps {
  /** @deprecated Billing now scopes to the caller's active workgroup via AuthContext; this prop is unused. */
  organisationId?: number;
}
export function Billing(_props: BillingProps = {}) {
  const { user, clientProfile, refreshUser, activeWorkgroupId, workgroupEpoch, workgroups } = useAuth();
  const [currentPackage, setCurrentPackage] = useState<Subscription | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [invoiceHistory, setInvoiceHistory] = useState<Invoice[]>([]);
  const [showPackages, setShowPackages] = useState(false);
  const [showContactSales, setShowContactSales] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [isUpdatingAddress, setIsUpdatingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState<BillingAddressData>({
    title: '',
    address: '',
    city: '',
    statename: '',
    pincode: '',
    country: '',
  });

  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);

  const daysRemaining = currentPackage?.validupto
    ? Math.ceil(
        (new Date(currentPackage.validupto).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  const isExpired =
    currentPackage &&
    (currentPackage.status === "EXPIRED" || daysRemaining < 0);

  // Community is forever free -- its validupto is a far-future placeholder
  // (see ProfileService), so billing dates/countdowns are meaningless for it.
  const isCommunity = currentPackage?.package.code === "COMMUNITY";

  useEffect(() => {
    const loadBillingData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [currentPackageResponse, packagesResponse, invoicesResponse, paymentMethodsResponse] = await Promise.all([
          api.billing.getCurrentPackage(),
          api.billing.getPackages(),
          api.billing.getInvoiceHistory(),
          api.billing.getPaymentMethods()
        ]);

        if (currentPackageResponse.data) {
          setCurrentPackage(currentPackageResponse.data);
          setSelectedPackage(Number(currentPackageResponse.data.package.id));
        }

        if (packagesResponse.data) {
          setPackages(packagesResponse.data);
        }

        if (invoicesResponse.data) {
          setInvoiceHistory(invoicesResponse.data);
        }

        if (paymentMethodsResponse.data) {
          setPaymentMethods(paymentMethodsResponse.data);
        }
        if (clientProfile?.billingAddress) {
          setAddressForm({
            title: clientProfile.billingAddress.title,
            address: clientProfile.billingAddress.address,
            city: clientProfile.billingAddress.city,
            statename: clientProfile.billingAddress.statename,
            pincode: clientProfile.billingAddress.pincode,
            country: clientProfile.billingAddress.country,
          });
        }
      } catch (error) {
        setError('Failed to load billing information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadBillingData();
    }
    // Re-fetch billing data whenever the active workgroup changes so a
    // workgroup switch doesn't leave stale plan/invoice/payment data on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, clientProfile, activeWorkgroupId, workgroupEpoch]);

  const handlePackageChange = async (packageId: number) => {
    setIsUpdating(true);
    setError(null);

    try {
      const updateResponse = await api.billing.updatePackage(packageId);

      if (updateResponse.data?.requiresPayment) {
        const { clientSecret, paymentMethodId } = updateResponse.data;

        if (!clientSecret) {
          throw new Error('Payment setup failed. Please try again.');
        }

        const confirmResponse = await api.billing.confirmPayment(
          clientSecret.split('_secret_')[0],
          paymentMethodId!,
          packageId
        );

        if (confirmResponse.data?.status === 'succeeded') {
          const response = await api.billing.getCurrentPackage();
          if (response.data) {
            setCurrentPackage(response.data);
            setSelectedPackage(parseInt(response.data.id.toString()));
          }

          const invoicesResponse = await api.billing.getInvoiceHistory();
          if (invoicesResponse.data) {
            setInvoiceHistory(invoicesResponse.data);
          }

          setIsUpdating(false);
          setShowPackages(false);

          toast.success('Plan updated and payment processed successfully!');
        } else {
          throw new Error(`Payment failed with status: ${confirmResponse.data?.status}`);
        }
      } else {
        const [currentResponse, invoicesResponse] = await Promise.all([
          api.billing.getCurrentPackage(),
          api.billing.getInvoiceHistory()
        ]);

        if (currentResponse.data) {
          setCurrentPackage(currentResponse.data);
          setSelectedPackage(parseInt(currentResponse.data.id.toString()));
        }

        if (invoicesResponse.data) {
          setInvoiceHistory(invoicesResponse.data);
        }

        setIsUpdating(false);
        setShowPackages(false);

        toast.success('Plan updated successfully!');
      }
    } catch (error) {
      setIsUpdating(false);
      setError(error instanceof Error ? error.message : 'Failed to update plan');
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setError(null);

    try {
      await api.billing.cancelSubscription();

      const response = await api.billing.getCurrentPackage();
      if (response.data) {
        setCurrentPackage(response.data);
      }

      setIsCancelling(false);
      toast.success('Subscription cancellation processed. You will continue to have access until your current billing period ends.');
    } catch (error) {
      setIsCancelling(false);
      setError(error instanceof Error ? error.message : 'Failed to cancel subscription');
    }
  };

  const handleAddPaymentMethod = async () => {
    setIsLoadingPaymentMethods(true);
    try {
      const response = await api.billing.createSetupIntent();
      if (response.data?.client_secret) {
        setSetupIntentSecret(response.data.client_secret);
        setShowAddPaymentDialog(true);
      }
    } catch (error) {
      toast.error('Failed to initialize payment method setup');
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const handlePaymentMethodSuccess = async (paymentMethodId: string) => {
    const response = await api.billing.getPaymentMethods();
    if (response.data) {
      setPaymentMethods(response.data);
    }
    setShowAddPaymentDialog(false);
    setSetupIntentSecret(null);
  };

  const handleRemovePaymentMethod = async (paymentMethodId: string) => {
    try {
      await api.billing.detachPaymentMethod(paymentMethodId);

      const response = await api.billing.getPaymentMethods();
      if (response.data) {
        setPaymentMethods(response.data);
      }
    } catch (error) {
      toast.error('Failed to remove payment method');
    }
  };

  const handleUpdateAddress = async () => {
    setIsUpdatingAddress(true);
    setError(null);

    try {
      if (!activeWorkgroupId) {
        throw new Error('No active workgroup found');
      }

      await api.billing.updateBillingAddress(activeWorkgroupId, addressForm);

      setIsUpdatingAddress(false);
      setShowAddressDialog(false);

      await refreshUser();

      toast.success('Billing address updated successfully!');

    } catch (error) {
      setIsUpdatingAddress(false);
      setError(error instanceof Error ? error.message : 'Failed to update billing address');
    }
  };

  const handleAddressInputChange = (field: keyof BillingAddressData, value: string) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const downloadInvoice = async (invoice: Invoice) => {
    try {
      if (!invoice.orderId) {
        toast.error('Order ID not available for this invoice');
        return;
      }

      const arrayBuffer = await api.billing.generateInvoice(invoice.orderId);

      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

        {error && (
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
            <AlertDescription className="text-red-800 dark:text-red-200 flex items-center justify-between">
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError(null)}
                className="ml-2 h-auto p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                ×
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!currentPackage?.isactive && (
          <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
            <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            <AlertDescription className="text-yellow-800 dark:text-yellow-200 flex items-center justify-between">
              <span>
                {currentPackage && currentPackage.validupto ? (
                  <>
                    Your subscription expired on{" "}
                    <strong>{formatBillingDate(currentPackage.validupto)}</strong>
                    . Renew your current plan or choose a different plan to continue using ORbiT8.
                  </>
                ) : (
                  "No active subscription found. Please select a plan to continue."
                )}
              </span>
              <Button
                size="sm"
                onClick={() => setShowPackages(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg ml-4"
              >
                Select a Plan
              </Button>
            </AlertDescription>
          </Alert>)
        }

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          <div className="lg:col-span-2">

            {currentPackage?.isactive && (
              <Card className="bg-card shadow-sm rounded-lg border-0 mb-6">
                <CardHeader className="pb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-5 h-5 text-primary" />
                      <CardTitle>Current Plan</CardTitle>
                    </div>
                    {currentPackage.status == "ACTIVE" && new Date(currentPackage.validupto) >= new Date() && (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    )}

                    {currentPackage.status == "CANCELLED" && (
                      <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        Cancelled
                      </Badge>
                    )}

                    {(currentPackage.status == "EXPIRED" || new Date(currentPackage.validupto) < new Date()) && (
                      <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                        <Clock className="w-3 h-3 mr-1" />
                        Expired
                      </Badge>
                    )}

                    {currentPackage.status == "PAYMENT PENDING" && (
                      <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        Payment Pending
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-2xl font-semibold text-foreground">{currentPackage.package.title}</h3>
                        <p className="text-muted-foreground">{currentPackage.package.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          ${currentPackage.package.price}<span className="text-sm font-normal text-muted-foreground">/{currentPackage.package.validity} days</span>
                        </div>
                      </div>
                    </div>

                    {!isCommunity && (
                      <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {isExpired ? "Expired On" : "Next Billing Date"}
                          </p>
                          <p className="font-medium">
                            {currentPackage.validupto
                              ? formatBillingDate(currentPackage.validupto)
                              : 'Not available'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {isExpired ? "Subscription Status" : "Days Until Billing"}
                          </p>
                          <p className="font-medium">
                            {isExpired
                              ? `Expired ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? "" : "s"} ago`
                              : daysRemaining === 0
                                ? "Expires today"
                                : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`}
                          </p>
                        </div>
                      </div>
                    )}

                    {currentPackage.packageaddons && currentPackage.packageaddons.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-foreground">Active Add-ons</h4>
                        <div className="grid gap-3">
                          {currentPackage.packageaddons.map((addon, index) => (
                            <div key={addon.id || index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className="font-medium text-sm">{addon.title}</h5>
                                  <Badge variant="secondary" className="text-xs">
                                    Add-on
                                  </Badge>
                                </div>
                                {addon.description && (
                                  <p className="text-xs text-muted-foreground mt-1">{addon.description}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="font-medium text-sm">
                                  ${addon.price}
                                  <span className="text-xs text-muted-foreground">/{addon.validity} days</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex space-x-3">
                      {(currentPackage.status == "EXPIRED" || new Date(currentPackage.validupto) < new Date()) ? (
                        <>
                          <Button
                            onClick={() => currentPackage && handlePackageChange(currentPackage.package.id)}
                            disabled={isUpdating}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
                          >
                            {isUpdating ? 'Processing...' : 'Renew Current Plan'}
                          </Button>
                          <Button
                            onClick={() => setShowPackages(!showPackages)}
                            variant="outline"
                            className="rounded-lg px-6"
                          >
                            Choose Different Plan
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => setShowPackages(!showPackages)}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
                          >
                            Change Plan
                          </Button>
                          {currentPackage.status == "ACTIVE" && new Date(currentPackage.validupto) >= new Date() && !isCommunity && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" className="rounded-lg px-6">
                                  Cancel Subscription
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-popover rounded-lg">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-700 dark:text-red-400">
                                    Cancel Subscription
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to cancel your subscription? You will continue to have access to your current plan until {currentPackage.validupto ? formatBillingDate(currentPackage.validupto) : 'the end of your billing period'}.
                                    <br /><br />
                                    After cancellation, you will lose access to:
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                      <li>Advanced scanning features</li>
                                      <li>Priority support</li>
                                      <li>Team collaboration tools</li>
                                      <li>Custom reporting capabilities</li>
                                    </ul>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-lg">Keep Subscription</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={handleCancelSubscription}
                                    disabled={isCancelling}
                                    className="bg-destructive hover:bg-destructive/90 rounded-lg"
                                  >
                                    {isCancelling ? "Cancelling..." : "Cancel Subscription"}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {currentPackage.status == "PAYMENT PENDING" && (
                            <Button
                              onClick={() => currentPackage && handlePackageChange(currentPackage.package.id)}
                              className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-6"
                            >
                              {isUpdating ? 'Updating...' : 'Pay Now'}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Dialog open={showPackages} onOpenChange={setShowPackages}>
              <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogTitle className="sr-only">Change your plan</DialogTitle>
                <DialogDescription className="sr-only">
                  Compare tiers and pick the plan that fits your scan volume.
                </DialogDescription>
                <PlanComparisonDialog
                  packages={packages}
                  currentPackage={currentPackage}
                  onChoosePlan={handlePackageChange}
                  onContactSales={() => setShowContactSales(true)}
                  isUpdating={isUpdating}
                />
              </DialogContent>
            </Dialog>

            <ContactSalesModal
              open={showContactSales}
              onOpenChange={setShowContactSales}
              title="Request the Enterprise edition"
              defaultFullName={`${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim()}
              defaultOrganisationName={workgroups.find((w) => w.id === activeWorkgroupId)?.title ?? ''}
              defaultWorkEmail={user?.emailid ?? ''}
              onSubmit={(data) => api.billing.contactSales(data).then(() => {})}
            />

            <Card className="bg-card shadow-sm rounded-lg border-0">
              <CardHeader className="pb-6">
                <CardTitle>Invoice History</CardTitle>
                <CardDescription>
                  Download your past invoices and payment records
                </CardDescription>
              </CardHeader>
              <CardContent>
                {invoiceHistory.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-muted-foreground">No invoices found</p>
                  </div>
                )}

                {invoiceHistory.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceHistory.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.id}</TableCell>
                          <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                          <TableCell>{invoice.description}</TableCell>
                          <TableCell>{invoice.amount}</TableCell>
                          <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadInvoice(invoice)}
                              className="rounded-lg"
                            >
                              <Download className="w-3 h-3 mr-1" />

                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {currentPackage?.isactive && currentPackage.validupto && !isCommunity && (
              <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  {isExpired ? (
                    <span>
                      Your subscription expired on{" "}
                      <strong>{formatBillingDate(currentPackage.validupto)}</strong>
                      . Renew your current plan or choose a different plan to continue using Orbit8.
                    </span>
                  ) : (
                    <span>
                      Your next billing date is in{" "}
                      <strong>{daysRemaining} day{daysRemaining === 1 ? "" : "s"}</strong>
                      . We'll charge your default payment method.
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* <Card className="bg-card shadow-sm rounded-lg border-0">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Entitlements</CardTitle>
                <CardDescription>What your current plan unlocks, independent of your role</CardDescription>
              </CardHeader>
              <CardContent>
                <EntitlementsCard />
              </CardContent>
            </Card> */}

            <Card className="bg-card shadow-sm rounded-lg border-0">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  Payment Methods
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                    style={{ backgroundColor: '#054f63' }}
                  >
                    Coming Soon
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {paymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <PaymentMethodCard
                        key={method.id}
                        paymentMethod={method}
                        onRemove={handleRemovePaymentMethod}
                        isDefault={method.id === paymentMethods[0]?.id}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p>No payment methods added</p>
                    <p className="text-sm">Add a payment method to manage your subscription</p>
                  </div>
                )}

                {/* Adding a new payment method is paused for now -- see "Add Payment Method" dialog below. */}
                <Button
                  disabled
                  className="w-full mt-4 rounded-lg"
                >
                  Add Payment Method
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Payment methods are temporarily unavailable.
                </p>

                <div className="p-3 border border-border rounded-lg bg-muted/50 mt-4 text-center">

                  <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">

                    <span>Powered by Stripe</span>
                  </div>
                    <p className="text-xs text-muted-foreground mb-2">
                   Disclaimer - Card details and payments are securely processed by Stripe, a trusted payment provider. We do not store your card information on our servers.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* <Dialog open={showAddPaymentDialog} onOpenChange={setShowAddPaymentDialog}>
              <DialogContent className="bg-popover rounded-lg max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Payment Method</DialogTitle>
                  <DialogDescription>
                    Enter your card details to add a new payment method.
                  </DialogDescription>
                </DialogHeader>
                {setupIntentSecret && (
                  <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret }}>
                    <PaymentForm
                      onSuccess={handlePaymentMethodSuccess}
                      isSetupMode={true}
                      clientSecret={setupIntentSecret}
                    />
                  </Elements>
                )}
              </DialogContent>
            </Dialog> */}

            {/* <Card className="bg-card shadow-sm rounded-lg border-0">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Billing Address</CardTitle>
              </CardHeader>
              <CardContent>
                {clientProfile?.billingAddress ? (
                  <div className="text-sm space-y-1">
                    <p>{clientProfile.billingAddress.title}</p>
                    <p>{clientProfile.billingAddress.address}</p>
                    <p>{clientProfile.billingAddress.city}, {clientProfile.billingAddress.statename} {clientProfile.billingAddress.pincode}</p>
                    <p>{COUNTRIES.find(country => country.value === clientProfile.billingAddress?.country)?.label}</p>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    <p>No billing address set</p>
                  </div>
                )}

                <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full mt-4 rounded-lg">
                      {clientProfile?.billingAddress ? 'Update Address' : 'Add Address'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-popover rounded-lg max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {clientProfile?.billingAddress ? 'Update' : 'Add'} Billing Address
                      </DialogTitle>
                      <DialogDescription>
                        {clientProfile?.billingAddress ? 'Update' : 'Add'} your organisation's billing address information.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="title">Organisation Name</Label>
                        <Input
                          id="title"
                          value={addressForm.title}
                          onChange={(e) => handleAddressInputChange('title', e.target.value)}
                          placeholder="Enter organisation name"
                          className="rounded-lg"
                          maxLength={100}
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="address">Address</Label>
                        <Input
                          id="address"
                          value={addressForm.address}
                          onChange={(e) => handleAddressInputChange('address', e.target.value)}
                          placeholder="Enter street address"
                          className="rounded-lg"
                          maxLength={200}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="grid gap-2">
                          <Label htmlFor="city">City</Label>
                          <Input
                            id="city"
                            value={addressForm.city}
                            onChange={(e) => handleAddressInputChange('city', e.target.value)}
                            placeholder="Enter city"
                            className="rounded-lg"
                            maxLength={100}
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="statename">State</Label>
                          <Input
                            id="statename"
                            value={addressForm.statename}
                            onChange={(e) => handleAddressInputChange('statename', e.target.value)}
                            placeholder="Enter state"
                            className="rounded-lg"
                            maxLength={100}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="grid gap-2">
                          <Label htmlFor="pincode">PIN Code</Label>
                          <Input
                            id="pincode"
                            value={addressForm.pincode}
                            onChange={(e) => handleAddressInputChange('pincode', e.target.value)}
                            placeholder="Enter PIN code"
                            className="rounded-lg"
                            maxLength={10}
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="country">Country</Label>

                          <Select value={addressForm.country} onValueChange={(value) => handleAddressInputChange('country', value)}>
                            <SelectTrigger className="rounded-lg border-border focus:border-primary focus:ring-primary">
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent>
                              {COUNTRIES.map((country) => (
                                <SelectItem key={country.value} value={country.value}>
                                  {country.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowAddressDialog(false)}
                        className="rounded-lg"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleUpdateAddress}
                        disabled={isUpdatingAddress}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
                      >
                        {isUpdatingAddress ? 'Updating...' : (clientProfile?.billingAddress ? 'Update Address' : 'Add Address')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card> */}

          </div>
        </div>
      </div>
  );
}